import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useSite } from '../context/SiteContext';
import axios from 'axios';
import { API_BASE_URL } from '../config/api';
import SiteCombobox from './SiteCombobox';

const TenantLayout = ({ children, userRole = 'tenant_admin' }) => {
  const location = useLocation();
  const { selectedSite, sites } = useSite();
  const [showProfileMenu, setShowProfileMenu] = React.useState(false);
  const [showChangePassword, setShowChangePassword] = React.useState(false);
  const [showAccountSettings, setShowAccountSettings] = React.useState(false);
  const [passwordForm, setPasswordForm] = React.useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [userInfo] = React.useState(JSON.parse(localStorage.getItem('user') || '{}'));
  const [siteFeatures, setSiteFeatures] = React.useState([]);
  
  React.useEffect(() => {
    if (selectedSite && selectedSite !== 'all' && sites && sites.length > 0) {
      const currentSite = sites.find(site => site._id === selectedSite);
      setSiteFeatures(currentSite?.features || []);
    } else {
      setSiteFeatures([]);
    }
  }, [selectedSite, sites]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    window.location.href = '/login';
  };
  
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('New passwords do not match');
      return;
    }
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API_BASE_URL}/api/auth/change-password`, {
        currentPassword: passwordForm.currentPassword,
        newPassword: passwordForm.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Password changed successfully');
      setShowChangePassword(false);
      setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      alert('Error changing password: ' + (error.response?.data?.message || error.message));
    }
  };
  
  const getNavigation = () => {
    const baseNav = [
      { id: 'dashboard', icon: '📊', label: 'Dashboard', path: '/tenant-dashboard' }
    ];

    if (userRole === 'tenant_admin') {
      const adminNav = [
        ...baseNav,
        { id: 'sites', icon: '🏛️', label: 'Sites', path: '/tenant-dashboard/sites' },
        { id: 'tickets', icon: '🎫', label: 'Tickets', path: '/tenant-dashboard/tickets' },
        { id: 'bookings', icon: '📋', label: 'Bookings', path: '/tenant-dashboard/bookings' },
        { id: 'monthly-passes', icon: '🎟️', label: 'Monthly Passes', path: '/tenant-dashboard/monthly-passes' }
      ];
      
      if (siteFeatures.includes('function_hall_ticket')) {
        adminNav.push(
          { id: 'function-hall-calendar', icon: '📅', label: 'Hall Calendar', path: '/tenant-dashboard/function-hall-calendar' }
        );
      }
      
      adminNav.push(
        { id: 'staff', icon: '👥', label: 'Staff', path: '/tenant-dashboard/staff' },
        { id: 'analytics', icon: '📈', label: 'Analytics', path: '/tenant-dashboard/analytics' },
        { id: 'settings', icon: '⚙️', label: 'Settings', path: '/tenant-dashboard/settings' }
      );
      
      return adminNav;
    } else if (userRole === 'ticketing_staff') {
      return [
        ...baseNav,
        { id: 'booking', icon: '🎫', label: 'Book Tickets', path: '/staff-dashboard/booking' },
        { id: 'print', icon: '🖨️', label: 'Print Queue', path: '/staff-dashboard/print' }
      ];
    } else if (userRole === 'security') {
      return [
        ...baseNav,
        { id: 'scanner', icon: '📱', label: 'QR Scanner', path: '/security-dashboard/scanner' },
        { id: 'visitors', icon: '👥', label: 'Visitors', path: '/security-dashboard/visitors' }
      ];
    }
    return baseNav;
  };

  const navigation = getNavigation();

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f8fafc 0%, #e0f2fe 50%, #f0fdfa 100%)', display: 'flex' }}>
      <div style={{ width: '256px', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRight: '1px solid rgba(6, 182, 212, 0.1)', boxShadow: '0 10px 40px rgba(6, 182, 212, 0.1)' }}>
        <div style={{ padding: '24px', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{ width: '48px', height: '48px', background: 'linear-gradient(135deg, #06b6d4, #10b981)', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 25px rgba(6, 182, 212, 0.3)' }}>
              <span style={{ color: 'white', fontWeight: '700', fontSize: '1.25rem' }}>🏛️</span>
            </div>
            <div>
              <h2 style={{ fontSize: '1.25rem', fontWeight: '700', color: '#1e293b' }}>
                {userRole === 'tenant_admin' ? 'Tenant Admin' : 
                 userRole === 'ticketing_staff' ? 'Ticketing Staff' : 'Security Staff'}
              </h2>
              <p style={{ fontSize: '0.875rem', color: '#64748b' }}>Dashboard</p>
            </div>
          </div>
        </div>

        {userRole === 'tenant_admin' && (
          <div style={{ padding: '16px 24px', borderBottom: '1px solid rgba(6, 182, 212, 0.1)' }}>
            <span className="text-sm font-medium text-gray-700 block mb-2">Filter by Site:</span>
            <SiteCombobox />
          </div>
        )}

        <nav style={{ marginTop: '24px' }}>
          {navigation.map(item => (
            <Link
              key={item.id}
              to={item.path}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'center',
                padding: '12px 24px',
                margin: '0 8px',
                borderRadius: '12px',
                textDecoration: 'none',
                transition: 'all 0.3s ease',
                ...(location.pathname === item.path 
                  ? {
                      background: 'linear-gradient(135deg, #06b6d4, #10b981)',
                      color: 'white',
                      boxShadow: '0 8px 25px rgba(6, 182, 212, 0.3)'
                    }
                  : {
                      color: '#64748b'
                    })
              }}
              onMouseOver={(e) => {
                if (location.pathname !== item.path) {
                  e.target.style.background = 'rgba(6, 182, 212, 0.05)';
                  e.target.style.color = '#06b6d4';
                }
              }}
              onMouseOut={(e) => {
                if (location.pathname !== item.path) {
                  e.target.style.background = 'transparent';
                  e.target.style.color = '#64748b';
                }
              }}
            >
              <span style={{ fontSize: '1.125rem', marginRight: '12px' }}>{item.icon}</span>
              <span style={{ fontWeight: '500' }}>{item.label}</span>
            </Link>
          ))}
        </nav>

        <div style={{ position: 'absolute', bottom: 0, width: '256px', padding: '24px', borderTop: '1px solid rgba(6, 182, 212, 0.1)', background: 'rgba(255,255,255,0.9)' }}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <div style={{ width: '32px', height: '32px', background: 'linear-gradient(135deg, #06b6d4, #10b981)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <span style={{ color: 'white', fontSize: '0.875rem' }}>👤</span>
              </div>
              <div>
                <p style={{ fontSize: '0.875rem', fontWeight: '500', color: '#1e293b' }}>{JSON.parse(localStorage.getItem('user') || '{}').name || 'User'}</p>
                <p style={{ fontSize: '0.75rem', color: '#10b981' }}>Online</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button 
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                style={{
                  flex: 1,
                  background: 'rgba(6, 182, 212, 0.1)',
                  color: '#06b6d4',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(6, 182, 212, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(6, 182, 212, 0.1)';
                }}
              >
                Profile
              </button>
              <button 
                onClick={handleLogout}
                style={{
                  flex: 1,
                  background: 'rgba(239, 68, 68, 0.1)',
                  color: '#dc2626',
                  padding: '8px 12px',
                  borderRadius: '8px',
                  fontSize: '0.75rem',
                  fontWeight: '500',
                  border: 'none',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease'
                }}
                onMouseOver={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                }}
                onMouseOut={(e) => {
                  e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                }}
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex flex-col">
        {userRole === 'tenant_admin' && (
          <div style={{ background: 'rgba(255,255,255,0.95)', backdropFilter: 'blur(10px)', borderBottom: '1px solid rgba(6, 182, 212, 0.1)', padding: '12px 24px', boxShadow: '0 4px 15px rgba(6, 182, 212, 0.05)' }}>
            <div className="flex items-center justify-end">
              <div className="flex items-center gap-2">
                <div className="relative">
                  <button 
                    onClick={() => setShowProfileMenu(!showProfileMenu)}
                    className="bg-gray-100 hover:bg-gray-200 text-gray-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                  >
                    👤 Profile
                  </button>
                  {showProfileMenu && (
                    <div className="fixed right-6 top-16 bg-white border border-gray-200 rounded-lg shadow-lg py-2 w-48 z-[9998]">
                      <button 
                        onClick={() => {setShowChangePassword(true); setShowProfileMenu(false);}}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        🔑 Change Password
                      </button>
                      <button 
                        onClick={() => {setShowAccountSettings(true); setShowProfileMenu(false);}}
                        className="w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                      >
                        ⚙️ Account Settings
                      </button>
                    </div>
                  )}
                </div>
                <button 
                  onClick={handleLogout}
                  className="bg-red-100 hover:bg-red-200 text-red-700 px-3 py-2 rounded-lg text-sm font-medium transition-colors"
                >
                  🚪 Logout
                </button>
              </div>
            </div>
          </div>
        )}
        {children}
        
        {showChangePassword && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-bold mb-4">Change Password</h3>
              <form onSubmit={handleChangePassword}>
                <div className="space-y-4">
                  <input
                    type="password"
                    placeholder="Current Password"
                    value={passwordForm.currentPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, currentPassword: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="password"
                    placeholder="New Password"
                    value={passwordForm.newPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="password"
                    placeholder="Confirm New Password"
                    value={passwordForm.confirmPassword}
                    onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                </div>
                <div className="flex gap-3 mt-6">
                  <button
                    type="submit"
                    className="flex-1 bg-green-500 hover:bg-green-600 text-white py-2 rounded-lg font-medium transition-colors"
                  >
                    Change Password
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowChangePassword(false)}
                    className="flex-1 bg-gray-300 hover:bg-gray-400 text-gray-700 py-2 rounded-lg font-medium transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
        
        {showAccountSettings && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
            <div className="bg-white rounded-lg p-6 w-96">
              <h3 className="text-lg font-bold mb-4">Account Settings</h3>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={userInfo.name || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={userInfo.email || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Role</label>
                  <input
                    type="text"
                    value={userInfo.role || ''}
                    readOnly
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-50"
                  />
                </div>
              </div>
              <div className="flex justify-end mt-6">
                <button
                  onClick={() => setShowAccountSettings(false)}
                  className="bg-gray-300 hover:bg-gray-400 text-gray-700 px-4 py-2 rounded-lg font-medium transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default TenantLayout;